import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

/**
 * Returns whether the current user has completed only one (or zero)
 * assessments so far — i.e. the assessment they just finished is
 * their very first.
 *
 * `isFirst` is `true` when `assessment_modules` has ≤ 1 row with
 * status = 'completed' for the user.
 */
export function useIsFirstAssessment() {
  const { user } = useAuth();
  const [isFirst, setIsFirst] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const check = async () => {
      try {
        const { count, error } = await supabase
          .from('assessment_modules')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'completed');

        if (error) throw error;
        setIsFirst((count ?? 0) <= 1);
      } catch (err) {
        logger.error('useIsFirstAssessment error:', err);
        setIsFirst(false); // default to existing messaging on error
      }
    };

    check();
  }, [user?.id]);

  return isFirst;
}
